import { InputRule, Node } from '@tiptap/core';
import { backtickInputRegex, tildeInputRegex } from '@tiptap/extension-code-block';
import type { NodeType } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';
import { NodeSelection } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ZyCodeNodeView from './zycode-node-view';

export interface ZyCodeStorage {
    pendingFocus: boolean;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zyCode: {
            /**
             * Insert a zyCode block
             */
            insertZyCode: (options?: InsertZyCodeOptions) => ReturnType;
            /**
             * Update zyCode attributes
             */
            updateZyCode: (attributes: Partial<ZyCodeAttributes>) => ReturnType;
        };
    }

    interface Storage {
        zyCode: ZyCodeStorage;
    }
}

export interface ZyCodeAttributes {
    language?: string;
    obeyleadingwhitespace?: boolean;
    highlight?: string;
    nobox?: boolean;
    numbering?: boolean;
}

export interface InsertZyCodeOptions extends ZyCodeAttributes {
    content?: string;
    focusOnInsert?: boolean;
}

const DEFAULT_LANGUAGE = 'python';

const createZyCodeInputRuleHandler = (nodeType: NodeType, onInsert: () => void) => {
    return ({ state, range }: { state: EditorState; range: { from: number; to: number } }) => {
        const { tr } = state;
        const start = range.from;
        const end = range.to;
        const node = nodeType.create({
            language: DEFAULT_LANGUAGE,
            obeyleadingwhitespace: false,
            highlight: null,
            nobox: false,
            numbering: false,
        });
        tr.replaceWith(start - 1, end, node);
        tr.setSelection(NodeSelection.near(tr.doc.resolve(start - 1)));
        onInsert();
    };
};

export const ZyCodeHeadless = Node.create({
    name: 'zyCode',

    group: 'block',

    content: 'text*',

    atom: true,

    selectable: true,

    draggable: true,

    addStorage() {
        return {
            pendingFocus: false,
        };
    },

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('id'),
                renderHTML: attributes => ({
                    id: attributes.id,
                }),
            },
            language: {
                default: null,
                parseHTML: element => element.getAttribute('language')?.toLowerCase() ?? null,
                renderHTML: attributes => {
                    if (!attributes.language) {
                        return {};
                    }
                    return {
                        language: attributes.language,
                    };
                },
            },
            obeyleadingwhitespace: {
                default: false,
                parseHTML: element => element.getAttribute('obeyleadingwhitespace') === 'true',
                renderHTML: attributes => {
                    if (!attributes.obeyleadingwhitespace) {
                        return {};
                    }
                    return {
                        obeyleadingwhitespace: 'true',
                    };
                },
            },
            highlight: {
                default: null,
                parseHTML: element => element.getAttribute('highlight'),
                renderHTML: attributes => {
                    if (!attributes.highlight) {
                        return {};
                    }
                    return {
                        highlight: attributes.highlight,
                    };
                },
            },
            nobox: {
                default: false,
                parseHTML: element => element.getAttribute('nobox') === 'true',
                renderHTML: attributes => {
                    if (!attributes.nobox) {
                        return {};
                    }
                    return {
                        nobox: 'true',
                    };
                },
            },
            numbering: {
                default: false,
                parseHTML: element => element.getAttribute('numbering') === 'true',
                renderHTML: attributes => {
                    if (!attributes.numbering) {
                        return {};
                    }
                    return {
                        numbering: 'true',
                    };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'zyCode',
                preserveWhitespace: 'full',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyCode', HTMLAttributes, ['code', {}, 0]];
    },

    addCommands() {
        return {
            insertZyCode:
                (options: InsertZyCodeOptions = {}) =>
                ({ commands }) => {
                    const content = options.content || '';
                    const focusOnInsert = options.focusOnInsert === undefined ? true : options.focusOnInsert;
                    if (focusOnInsert) {
                        this.storage.pendingFocus = true;
                    }
                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            language: options.language || DEFAULT_LANGUAGE,
                            obeyleadingwhitespace: options.obeyleadingwhitespace || false,
                            highlight: options.highlight || null,
                            nobox: options.nobox || false,
                            numbering: options.numbering || false,
                        },
                        content: content
                            ? [
                                  {
                                      type: 'text',
                                      text: content,
                                  },
                              ]
                            : undefined,
                    });
                },
            updateZyCode:
                (attributes: Partial<ZyCodeAttributes>) =>
                ({ commands }) => {
                    return commands.updateAttributes(this.name, attributes);
                },
        };
    },
    addInputRules() {
        const zyCodeInputRuleHandler = createZyCodeInputRuleHandler(this.type, () => {
            this.storage.pendingFocus = true;
        });
        return [
            new InputRule({
                find: backtickInputRegex,
                handler: zyCodeInputRuleHandler,
            }),
            new InputRule({
                find: tildeInputRegex,
                handler: zyCodeInputRuleHandler,
            }),
        ];
    },
});

export const ZyCode = ZyCodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyCodeNodeView);
    },
});

export default ZyCode;
